import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { CircleArrowRightIcon, EyeIcon } from "lucide-react";

import type { ChallengeYourIdea } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { belowComputer, computerUp, useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H2, JustifiedHeader, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import IdeasImage from "src/images/ideas.png";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";
import IdeaCardView from "components/lib/Ideas/IdeaView/IdeaCardView";
import type { LibIdeaViewIdea } from "components/lib/Ideas/types";
import { OrgSwitcherDropdown } from "components/lib/OrgSwitcherTray";
import { EmptyBox, PaginationWithLimiter } from "components/lib/UI";
import { parseFollowingItem } from "components/users/types";

export const SideNavContainer = styled.div`
  ${belowComputer} {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    width: 100%;
    margin: 12.5px 0 2.5px 0;
  }

  ${computerUp} {
    margin-top: 10px;
  }
`;

const FollowedList = styled.div`
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.1);
  border-radius: 10px;
  overflow: hidden;
`;

const FollowedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(34, 36, 38, 0.08);
  }

  ${belowComputer} {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

const FollowedCover = styled.img`
  width: 64px;
  height: 44px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: #f4f1e8;
`;

const FollowedMeta = styled.div`
  min-width: 0;
  flex: 1;
`;

const FollowedName = styled.div`
  font-weight: 600;
  color: #3b3b3b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FollowedDetail = styled.div`
  margin-top: 2px;
  color: #767676;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const matchesSearch = (value: string | undefined, query: string) =>
  !!value?.toLowerCase().includes(query);

const personNames = (person?: {
  profile?: { firstName?: string; lastName?: string };
}) => {
  const firstName = person?.profile?.firstName;
  const lastName = person?.profile?.lastName;
  return [firstName, lastName, [firstName, lastName].filter(Boolean).join(" ")];
};

const ideaMatchesQuery = (idea: ChallengeYourIdea, query: string) => {
  if (!query) return true;
  const withOwners = idea as ChallengeYourIdea & {
    authors?: { profile?: { firstName?: string; lastName?: string } }[];
    owner?: { profile?: { firstName?: string; lastName?: string } };
    lockedOwnerIdeaBusinessProfile?: { name?: string };
  };
  const ownerNames = [
    withOwners.lockedOwnerIdeaBusinessProfile?.name,
    ...personNames(withOwners.owner),
    ...(withOwners.authors?.flatMap((author) => personNames(author)) ?? []),
  ];
  return (
    matchesSearch(idea.name, query) ||
    ownerNames.some((name) => matchesSearch(name, query))
  );
};

const FOLLOWING_FETCH_SIZE = 25;
const FOLLOWED_PAGE_SIZE = 25;

const followedIdeaFromItem = (
  item: Parameters<typeof parseFollowingItem>[0],
  ownIdeaIds: Set<string>,
  userId: string,
  organisationId?: string,
) => {
  const followingItem = parseFollowingItem(item);
  if (
    followingItem.type !== "idea" ||
    followingItem.isDeleted ||
    followingItem.unfollowed
  ) {
    return;
  }
  const idea = followingItem.ownerIdea as ChallengeYourIdea | undefined;
  if (!idea?._id || !idea.ownerChallenge) return;
  if (ownIdeaIds.has(idea._id) || idea.user === userId) return;
  if (idea.ownerChallenge.organisation !== organisationId) return;
  return idea;
};

const YourIdeas = () => {
  const [ideas, setIdeas] = useState<ChallengeYourIdea[]>([]);
  const [followedIdeas, setFollowedIdeas] = useState<ChallengeYourIdea[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState("");
  const [followedPage, setFollowedPage] = useState(1);
  const [previewIdeaId, setPreviewIdeaId] = useState<string | null>(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const userId = user?._id;
  const organisationId = user?.ownerOrganisation?._id;

  useEffect(() => {
    setIdeas([]);
    setFollowedIdeas([]);
    setLoadFailed(false);
    if (!userId) {
      return () => {
        api.journey.record("ideas", null, "viewIdeasPage");
      };
    }

    let cancelled = false;
    setLoading(true);

    const finish = (
      ownIdeas: ChallengeYourIdea[],
      followed: ChallengeYourIdea[],
    ) => {
      if (cancelled) return;
      setIdeas(ownIdeas);
      setFollowedIdeas(followed);
      setLoading(false);
    };

    api.users.ideas(
      userId,
      null,
      (data) => {
        const ownIdeas = data.ideas ?? [];
        const ownIdeaIds = new Set(ownIdeas.map((idea) => idea._id));

        const loadFollowing = (page: number, acc: ChallengeYourIdea[]) => {
          api.users.getFollowing(
            userId,
            { page, limit: FOLLOWING_FETCH_SIZE },
            ({ following, nextPageAvailable }) => {
              if (cancelled) return;
              const pageIdeas = following.flatMap((item) => {
                const idea = followedIdeaFromItem(
                  item,
                  ownIdeaIds,
                  userId,
                  organisationId,
                );
                return idea ? [idea] : [];
              });
              const next = acc.concat(pageIdeas);
              if (nextPageAvailable) {
                loadFollowing(page + 1, next);
                return;
              }
              finish(ownIdeas, next);
            },
            (err) => {
              if (cancelled) return;
              toast.error(err.message);
              finish(ownIdeas, acc);
            },
          );
        };

        loadFollowing(1, []);
      },
      (err) => {
        if (cancelled) return;
        toast.error(err.message);
        setLoadFailed(true);
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
      api.journey.record("ideas", null, "viewIdeasPage");
    };
  }, [userId, organisationId]);

  const query = filter.trim().toLowerCase();
  const filteredIdeas = ideas.filter((idea) => ideaMatchesQuery(idea, query));
  const filteredFollowed = followedIdeas.filter((idea) =>
    ideaMatchesQuery(idea, query),
  );
  const visibleFollowedPage = Math.min(
    followedPage,
    Math.max(1, Math.ceil(filteredFollowed.length / FOLLOWED_PAGE_SIZE)),
  );
  const visibleFollowed = filteredFollowed.slice(
    (visibleFollowedPage - 1) * FOLLOWED_PAGE_SIZE,
    visibleFollowedPage * FOLLOWED_PAGE_SIZE,
  );
  const hasAny = ideas.length > 0 || followedIdeas.length > 0;
  const hasVisible = filteredIdeas.length > 0 || filteredFollowed.length > 0;
  const splitColumns =
    filteredIdeas.length > 0 &&
    filteredFollowed.length > 0 &&
    theme.sizes.isComputer;

  return (
    <div>
      <Helmet title={t("challenges.yours.ideas.title")} />
      <HandledIdeaPreview
        ideaId={previewIdeaId}
        onCloseModal={() => setPreviewIdeaId(null)}
      />

      <div style={{ marginTop: "2rem" }}>
        <Container>
          <JustifiedHeader>
            <div>
              <H1 style={{ color: "#3b3b3b", margin: "0px" }}>
                {t("challenges.yours.ideas.title")}
              </H1>
              {(user?.ownerOrganisations?.length ?? 0) > 1 ? (
                <div
                  style={{
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "baseline",
                    gap: "0.25em",
                  }}
                >
                  <span>
                    in{" "}
                    <span style={{ fontWeight: 600 }}>
                      {user?.ownerOrganisation?.name}
                    </span>
                    {" -"}
                  </span>
                  <OrgSwitcherDropdown
                    basic
                    trigger={
                      <a
                        tabIndex={0}
                        role="button"
                        onClick={(e) => e.preventDefault()}
                      >
                        Change organisation
                      </a>
                    }
                  />
                </div>
              ) : null}
            </div>
          </JustifiedHeader>
          <Input
            placeholder={t("ideas.yours.sidebar.search")}
            value={filter}
            onChange={(_e, { value }) => {
              setFilter(value);
              setFollowedPage(1);
            }}
            style={{ width: theme.sizes.isComputer ? "33%" : "100%" }}
          />
        </Container>
      </div>

      <Container style={{ marginTop: "1rem" }}>
        {loading && !hasVisible && <Loader />}
        {!loading && !loadFailed && !hasAny && (
          <EmptyBox>
            <img
              src={IdeasImage}
              style={{
                maxHeight: 150,
                maxWidth: "70%",
                display: "block",
                margin: "10px auto",
              }}
              alt="Discover"
            />
            <H2>
              {t("ideas.yours.sidebar.noneCreated")}
              <Subheader>{t("ideas.yours.sidebar.start")}</Subheader>
            </H2>
            <ButtonLink color="primary" to="/challenges/discover">
              <Icon icon={EyeIcon} />
              {t("challenges.discover.title")}
            </ButtonLink>
          </EmptyBox>
        )}
        {!loading && hasAny && !hasVisible ? (
          <EmptyBox>
            <img
              src={IdeasImage}
              style={{
                maxHeight: 150,
                maxWidth: "70%",
                display: "block",
                margin: "10px auto",
              }}
              alt="Discover"
            />
            <H2>{t("ideas.yours.sidebar.noResults")}</H2>
          </EmptyBox>
        ) : null}
        <Grid style={{ marginTop: "1rem" }} columns={splitColumns ? 2 : 1}>
          {filteredIdeas.length ? (
            <GridColumn>
              {splitColumns ? (
                <H2>{t("challenges.yours.ideas.subtitle")}</H2>
              ) : null}
              <Grid columns={splitColumns ? 2 : 4} mobile={1} gap="1rem">
                {filteredIdeas.map((idea) =>
                  idea.ownerChallenge ? (
                    <GridColumn key={idea._id}>
                      <IdeaCardView
                        idea={idea as LibIdeaViewIdea}
                        challenge={idea.ownerChallenge}
                        ideasVotedFor={0}
                        setPreviewOpen={
                          idea.isSubmitted
                            ? (ideaId) => setPreviewIdeaId(ideaId)
                            : undefined
                        }
                      />
                    </GridColumn>
                  ) : null,
                )}
              </Grid>
            </GridColumn>
          ) : null}
          {filteredFollowed.length ? (
            <GridColumn
              style={!theme.sizes.isComputer ? { marginTop: 20 } : {}}
            >
              <H2>{t("ideas.yours.followed")}</H2>
              <FollowedList>
                {visibleFollowed.map((idea) => (
                  <FollowedRow key={idea._id}>
                    <FollowedCover
                      src={util.ideaCoverImage(idea, "200x200")}
                      alt=""
                    />
                    <FollowedMeta>
                      <FollowedName>{idea.name}</FollowedName>
                      {idea.ownerChallenge?.name ? (
                        <FollowedDetail>
                          <Link
                            to={`/challenges/${idea.ownerChallenge._id}`}
                            style={{ color: theme.primaryColour }}
                          >
                            {idea.ownerChallenge.name}
                          </Link>
                        </FollowedDetail>
                      ) : null}
                    </FollowedMeta>
                    {idea.isSubmitted ? (
                      <Button
                        size="small"
                        variant="soft"
                        color="primary"
                        onClick={() => setPreviewIdeaId(idea._id)}
                        style={{ flexShrink: 0 }}
                      >
                        {t("ideas.yours.followedView")}
                        <Icon icon={CircleArrowRightIcon} />
                      </Button>
                    ) : (
                      <ButtonLink
                        size="small"
                        variant="soft"
                        color="primary"
                        to={`/ideas/${idea._id}`}
                        style={{ flexShrink: 0 }}
                      >
                        {t("ideas.yours.followedView")}
                        <Icon icon={CircleArrowRightIcon} />
                      </ButtonLink>
                    )}
                  </FollowedRow>
                ))}
              </FollowedList>
              {filteredFollowed.length > FOLLOWED_PAGE_SIZE ? (
                <PaginationWithLimiter
                  limit={FOLLOWED_PAGE_SIZE}
                  page={visibleFollowedPage}
                  total={filteredFollowed.length}
                  showLimiter={false}
                  updatePage={setFollowedPage}
                />
              ) : null}
            </GridColumn>
          ) : null}
        </Grid>
        <div style={{ marginTop: 30, textAlign: "center" }}>
          <Link
            to="/challenges/discover"
            style={{
              color: theme.primaryColour,
              textDecorationLine: "underline",
              bottom: 0,
            }}
          >
            {t("challenges.discover.title")}
          </Link>
        </div>
      </Container>
    </div>
  );
};

export default YourIdeas;
